import React, { useState } from "react";
import { Row, Col, Card, Button, Form, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "app/components/Breadcrumb";
import axios from "app/services/api";

const API_URL = "/api";

// Column order for the employee import file (headers must stay in this order)
const COLUMNS = [
  "First Name",
  "Last Name",
  "Staff Id",
  "Username",
  "Email",
  "Password",
  "Joining Date",
  "Gender",
  "Date of Birth",
  "Contact Number",
  "Address",
  "City",
  "Zip",
  "County",
  "Attendance Type",
  "Company Name",
  "Department Name",
  "Designation Name",
  "Shift Name",
  "Role Name"
];

// Small quote-aware CSV parser (handles commas & quotes inside double-quoted fields)
const parseCSV = (text) => {
  const rows = [];
  let field = "";
  let record = [];
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];

    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      record.push(field);
      field = "";
    } else if (c === "\n" || c === "\r") {
      // finish the record on newline (handle \r\n by skipping the paired char)
      if (c === "\r" && text[i + 1] === "\n") i++;
      record.push(field);
      rows.push(record);
      record = [];
      field = "";
    } else {
      field += c;
    }
  }
  // flush trailing field/record
  if (field !== "" || record.length > 0) {
    record.push(field);
    rows.push(record);
  }
  return rows;
};

export default function ImportEmployees() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  // Build & download a sample CSV template
  const handleDownloadSample = () => {
    const example = [
      "John",
      "Doe",
      "EMP101",
      "jdoe",
      "jdoe@acme.com",
      "Password@123",
      "2026-07-01",
      "Male",
      "1995-05-10",
      "9876543210",
      "12 Main St",
      "Pune",
      "411001",
      "India",
      "general",
      "Acme Corp",
      "Engineering",
      "Software Engineer",
      "General Shift",
      "Employee"
    ];
    const csv = COLUMNS.join(",") + "\n" + example.map((v) => `"${v}"`).join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "employees_sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e) => {
    setResult(null);
    setFile(e.target.files[0] || null);
  };

  const handleSave = () => {
    if (!file) {
      alert("Please select a CSV file first.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (ev) => {
      const text = ev.target.result;
      const parsed = parseCSV(text).filter((r) => r.some((c) => String(c).trim() !== ""));

      if (parsed.length < 2) {
        alert("The file has no data rows.");
        return;
      }

      const headers = parsed[0].map((h) => String(h).trim());
      const rows = parsed.slice(1).map((cells) => {
        const obj = {};
        headers.forEach((h, idx) => {
          obj[h] = cells[idx] != null ? String(cells[idx]).trim() : "";
        });
        return obj;
      });

      try {
        setSaving(true);
        setResult(null);
        const { data } = await axios.post(`${API_URL}/employees/import`, { rows });
        setResult(data);
      } catch (err) {
        console.error("Error importing employees:", err);
        alert("Error importing employees: " + (err.response?.data?.message || err.message));
      } finally {
        setSaving(false);
      }
    };
    reader.readAsText(file);
  };

  return (
    <div>
      <Breadcrumb
        routeSegments={[
          { name: "Employees", path: "/employees" },
          { name: "Import Employees" }
        ]}
      />

      <Card
        body
        style={{
          borderRadius: "10px",
          padding: "20px",
          marginTop: "20px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
        }}
      >
        <h5 style={{ marginBottom: "15px" }}>Import CSV file only</h5>

        {/* Download Sample Button */}
        <Row className="mb-3">
          <Col md="auto">
            <Button
              onClick={handleDownloadSample}
              style={{
                backgroundColor: "#6f42c1",
                borderColor: "#6f42c1",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                borderRadius: "5px",
                padding: "8px 16px"
              }}
            >
              <i className="i-Download"></i>
              Download Sample File
            </Button>
          </Col>
        </Row>

        {/* Instructions */}
        <p style={{ fontSize: "14px", color: "#959292ff", marginBottom: "10px" }}>
          The first line in downloaded sample file should remain as it is. Please do not change the order of columns in file.
        </p>
        <p style={{ fontSize: "14px", color: "#959292ff", marginBottom: "10px" }}>
          The correct column order is ({COLUMNS.join(", ")}).
        </p>
        <ul style={{ fontSize: "14px", color: "#0c0c0cff", paddingLeft: "20px", marginBottom: "20px" }}>
          <li>Date format should be YYYY-MM-DD (e.g. 2026-07-01).</li>
          <li>Company, Department, Designation, Shift and Role name must be matched with your existing data.</li>
          <li>Gender must be Male / Female / Other.</li>
          <li>First Name, Staff Id and Email are required.</li>
          <li>Staff Id and Email must be unique — existing ones will be skipped.</li>
          <li>If Password is left blank, a default password (Password@123) will be set.</li>
          <li>You must follow the file, otherwise you will get an error while importing the file.</li>
        </ul>

        {/* File Upload */}
        <Form.Group controlId="formFile" className="mb-3">
          <Form.Label>Upload File</Form.Label>
          <Form.Control
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{
              borderRadius: "5px",
              padding: "6px 12px"
            }}
          />
          <small style={{ fontSize: "12px", color: "#777" }}>
            Please select a csv file (allowed file size 2MB)
          </small>
        </Form.Group>

        {/* Save Button */}
        <Row>
          <Col md="auto">
            <Button
              onClick={handleSave}
              disabled={saving}
              style={{
                backgroundColor: "#6f42c1",
                borderColor: "#6f42c1",
                color: "#fff",
                borderRadius: "5px",
                padding: "8px 16px"
              }}
            >
              <i className="i-Data-Save" style={{ marginRight: "8px" }}></i>
              {saving ? "Importing..." : "Save"}
            </Button>
          </Col>
        </Row>

        {/* Result Summary */}
        {result && (
          <Alert variant={result.errors?.length ? "warning" : "success"} className="mt-4">
            <strong>{result.message}</strong>

            {result.skipped?.length > 0 && (
              <>
                <hr />
                <div style={{ fontSize: "13px" }}>
                  <strong>Skipped (duplicates):</strong>
                  <ul className="mb-0">
                    {result.skipped.map((s, idx) => (
                      <li key={`sk-${idx}`}>
                        Row {s.row}{s.staff_id ? ` (${s.staff_id})` : ""} — {s.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {result.errors?.length > 0 && (
              <>
                <hr />
                <div style={{ fontSize: "13px" }}>
                  <strong>Errors:</strong>
                  <ul className="mb-0">
                    {result.errors.map((e, idx) => (
                      <li key={`er-${idx}`}>
                        Row {e.row}{e.staff_id ? ` (${e.staff_id})` : ""} — {e.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {result.inserted > 0 && (
              <div className="mt-3">
                <Button
                  size="sm"
                  style={{ backgroundColor: "#663399", border: "none" }}
                  onClick={() => navigate("/employees/EmployeeLists")}
                >
                  View Employee List
                </Button>
              </div>
            )}
          </Alert>
        )}
      </Card>
    </div>
  );
}
